import { useEffect, useRef } from "react";
import { useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { Button } from "@nexus/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getDefaultRoute, routeToPageSlug } from "@/features/auth/lib/page-routes";
import { PageAccessError } from "@/lib/queries";

interface PageErrorProps {
  is403?: boolean;
  onReset?: () => void;
  resetLabel?: string;
}

/**
 * Route `errorComponent`. Re-runs beforeLoad and drops the error boundary when
 * the active tenant gains the current page — nested `<PageError is403 />`
 * tab stubs do not go through here, so they stay put.
 */
export function PageAccessErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const is403 = error instanceof PageAccessError;
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const recoveredForTenantRef = useRef<string | null>(null);

  useEffect(() => {
    if (!is403 || !activeTenantId) return;
    if (recoveredForTenantRef.current === activeTenantId) return;
    const slug = routeToPageSlug(pathname);
    if (!slug) return;
    const activeTenant = tenants.find((t) => t.id === activeTenantId);
    const hasPage =
      activeTenant?.permissions.some((p) => p.type === "PAGE" && p.route === slug) ?? false;
    if (!hasPage) return;
    recoveredForTenantRef.current = activeTenantId;
    void router.invalidate().then(reset);
  }, [is403, tenants, activeTenantId, pathname, router, reset]);

  return <PageError is403={is403} onReset={is403 ? reset : undefined} />;
}

export function PageError({ is403, onReset, resetLabel }: PageErrorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const activeTenants = tenants.filter((t) => t.id === activeTenantId);
  const defaultRoute = getDefaultRoute(activeTenants);

  // Clear the persisted last route once it's known to be inaccessible — otherwise
  // the root `/` redirect keeps sending the user back to this same dead-end page.
  useEffect(() => {
    if (is403) useAuth.getState().setLastVisitedRoute("");
  }, [is403]);

  if (is403) {
    const handleNavigateAway = () => {
      void navigate({ to: (defaultRoute ?? "/login") as never });
    };

    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <div className="rounded-full bg-[var(--muted)] p-6">
          <ShieldAlert className="h-10 w-10 text-[var(--muted-foreground)]" />
        </div>
        <h1 className="text-xl font-normal">{t("errors.noAccess")}</h1>
        <p className="max-w-md text-[var(--muted-foreground)]">
          {t("errors.noAccessDescription")}
        </p>
        <Button variant="outline" onClick={handleNavigateAway}>
          {defaultRoute ? t("errors.goToAvailablePage") : t("errors.backToLogin")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
      <div className="rounded-full bg-[var(--muted)] p-6">
        <AlertTriangle className="h-10 w-10 text-[var(--muted-foreground)]" />
      </div>
      <h1 className="text-xl font-normal">{t("errors.pageLoadFailed")}</h1>
      <p className="max-w-md text-[var(--muted-foreground)]">
        {t("errors.pageLoadFailedDescription")}
      </p>
      {onReset && (
        <Button onClick={onReset}>
          {resetLabel ?? t("errors.goBack", "Go back")}
        </Button>
      )}
    </div>
  );
}
